/**
 * What a knowledge-base duplex session shows, folded from server frames.
 *
 * Pure on purpose: the page needs a microphone, a socket and audio playback
 * to exercise, and the rules here are the ones worth pinning:
 *   - the current item is the one the customer raised last;
 *   - progress comes only from `item_answered` (see kbItemProgress);
 *   - the "Still there?" banner shows on silence `checking_in` and clears on
 *     `moved_on`, on the next item, or as soon as the rep speaks.
 *
 * The item's card is not here: card frames go into the live-call Redux
 * slices (see trainingCallActions) and render as on a call.
 */
import type {
  ItemAnsweredData,
  ItemRaisedData,
  SessionCompletedData,
  SessionStartedData,
  SilenceData,
} from '../services/kbLiveFrames';
import { EMPTY_KB_ITEM_PROGRESS, KbItemProgress, applyItemAnswered } from './kbItemProgress';

export interface KbLiveSessionState {
  trackName?: string;
  totalItems: number;
  progress: KbItemProgress;
  currentItem: ItemRaisedData | null;
  isCheckingIn: boolean;
  summary: SessionCompletedData | null;
}

export type KbLiveSessionAction =
  | { type: 'reset' }
  | { type: 'session_started'; data: SessionStartedData }
  | { type: 'item_raised'; data: ItemRaisedData }
  | { type: 'item_answered'; data: ItemAnsweredData }
  | { type: 'silence'; data: SilenceData }
  | { type: 'rep_spoke' }
  | { type: 'session_completed'; data: SessionCompletedData };

export const INITIAL_KB_LIVE_STATE: KbLiveSessionState = {
  totalItems: 0,
  progress: EMPTY_KB_ITEM_PROGRESS,
  currentItem: null,
  isCheckingIn: false,
  summary: null,
};

const knownTotal = (candidate: number, fallback: number): number =>
  candidate > 0 ? candidate : fallback;

export function kbLiveSessionReducer(
  state: KbLiveSessionState,
  action: KbLiveSessionAction,
): KbLiveSessionState {
  switch (action.type) {
    case 'reset':
      return INITIAL_KB_LIVE_STATE;
    case 'session_started':
      return {
        ...INITIAL_KB_LIVE_STATE,
        trackName: action.data.trackName,
        totalItems: action.data.totalItems,
        progress: { ...EMPTY_KB_ITEM_PROGRESS, totalItems: action.data.totalItems },
      };
    case 'item_raised':
      return {
        ...state,
        currentItem: action.data,
        isCheckingIn: false,
        totalItems: knownTotal(action.data.totalItems, state.totalItems),
      };
    case 'item_answered': {
      const progress = applyItemAnswered(state.progress, action.data);
      return {
        ...state,
        progress,
        isCheckingIn: false,
        totalItems: knownTotal(progress.totalItems, state.totalItems),
      };
    }
    case 'silence':
      return { ...state, isCheckingIn: action.data.stage === 'checking_in' };
    case 'rep_spoke':
      return state.isCheckingIn ? { ...state, isCheckingIn: false } : state;
    case 'session_completed':
      return { ...state, isCheckingIn: false, summary: action.data };
    default:
      return state;
  }
}
